import { useCallback, useMemo } from 'react'
import { scaleLinear, scaleTime } from '@visx/scale'
import { LinePath, AreaClosed, Bar, Line } from '@visx/shape'
import { curveStepAfter } from '@visx/curve'
import { AxisBottom, AxisLeft } from '@visx/axis'
import { GridRows } from '@visx/grid'
import { Group } from '@visx/group'
import { useTooltip } from '@visx/tooltip'
import { localPoint } from '@visx/event'
import type { PriceHistoryPoint } from '@/types'
import { formatPrice } from './PriceTag'

interface PriceChartProps {
  points: PriceHistoryPoint[]
  currency: string
  width?: number
  height?: number
}

interface ChartPoint {
  date: Date
  priceInt: number
  cut: number
}

const DEFAULT_W = 272
const DEFAULT_H = 168
const MARGIN = { top: 8, right: 12, bottom: 24, left: 40 }

const getDate = (d: ChartPoint) => d.date
const getPrice = (d: ChartPoint) => d.priceInt

export function PriceChart({
  points,
  currency,
  width = DEFAULT_W,
  height = DEFAULT_H,
}: PriceChartProps) {
  const W = width
  const H = height
  const INNER_W = W - MARGIN.left - MARGIN.right
  const INNER_H = H - MARGIN.top - MARGIN.bottom
  const {
    tooltipData,
    tooltipLeft = 0,
    tooltipTop = 0,
    tooltipOpen,
    showTooltip,
    hideTooltip,
  } = useTooltip<ChartPoint>()

  const data = useMemo<ChartPoint[]>(() => {
    if (points.length === 0) return []
    const sorted = [...points].sort((a, b) => a.timestamp - b.timestamp)
    const mapped = sorted.map((p) => ({
      date: new Date(p.timestamp * 1000),
      priceInt: p.priceInt,
      cut: p.cut,
    }))
    // Extend the last known price to "now" so the step holds to the right edge.
    const now = new Date()
    const last = mapped[mapped.length - 1]
    if (last.date.getTime() < now.getTime()) {
      mapped.push({ date: now, priceInt: last.priceInt, cut: last.cut })
    }
    return mapped
  }, [points])

  const xScale = useMemo(
    () =>
      scaleTime({
        range: [0, INNER_W],
        domain: [data[0]?.date ?? new Date(), data[data.length - 1]?.date ?? new Date()],
      }),
    [data, INNER_W],
  )

  const yScale = useMemo(() => {
    const prices = data.map(getPrice)
    const min = Math.min(...prices, 0)
    const max = Math.max(...prices, 1)
    const pad = (max - min) * 0.08 || max * 0.08
    return scaleLinear({
      range: [INNER_H, 0],
      domain: [Math.max(0, min - pad), max + pad],
      nice: true,
    })
  }, [data, INNER_H])

  const atl = useMemo(() => {
    if (data.length === 0) return null
    return data.reduce((lo, d) => (d.priceInt < lo.priceInt ? d : lo), data[0])
  }, [data])

  const formatAxisPrice = useCallback(
    (value: number) =>
      new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
        maximumFractionDigits: 0,
      }).format(value / 100),
    [currency],
  )

  // Evenly spaced ticks keep the axis uncrowded for any time span; wider
  // charts (longer histories) get a few more.
  const xTickValues = useMemo(() => {
    if (data.length === 0) return []
    const t0 = data[0].date.getTime()
    const t1 = data[data.length - 1].date.getTime()
    if (t1 === t0) return [data[0].date]
    const n = Math.min(6, Math.max(4, Math.round(INNER_W / 80)))
    return Array.from({ length: n }, (_, i) => new Date(t0 + ((t1 - t0) * i) / (n - 1)))
  }, [data, INNER_W])

  const fmtMonth = useMemo(() => new Intl.DateTimeFormat(undefined, { month: 'short' }), [])
  const formatAxisDate = useCallback(
    (d: Date) => `${fmtMonth.format(d)} '${String(d.getFullYear()).slice(2)}`,
    [fmtMonth],
  )

  const handleTooltip = useCallback(
    (event: React.MouseEvent<SVGRectElement> | React.TouchEvent<SVGRectElement>) => {
      const point = localPoint(event)
      if (!point) return
      const x0 = xScale.invert(point.x - MARGIN.left)
      // nearest data point by time
      let nearest = data[0]
      let best = Infinity
      for (const d of data) {
        const dist = Math.abs(d.date.getTime() - x0.getTime())
        if (dist < best) {
          best = dist
          nearest = d
        }
      }
      showTooltip({
        tooltipData: nearest,
        tooltipLeft: xScale(nearest.date),
        tooltipTop: yScale(nearest.priceInt),
      })
    },
    [data, xScale, yScale, showTooltip],
  )

  if (data.length === 0) {
    return <span className="cg-atl-tooltip-status">No history available</span>
  }

  return (
    <div className="cg-price-chart">
      <svg width={W} height={H} style={{ display: 'block' }}>
        <defs>
          <linearGradient id="cg-price-area" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--success)" stopOpacity="0.25" />
            <stop offset="100%" stopColor="var(--success)" stopOpacity="0.02" />
          </linearGradient>
        </defs>
        <Group left={MARGIN.left} top={MARGIN.top}>
          <GridRows
            scale={yScale}
            width={INNER_W}
            numTicks={3}
            stroke="var(--line)"
            strokeOpacity={0.4}
          />
          <AreaClosed<ChartPoint>
            data={data}
            x={(d) => xScale(getDate(d))}
            y={(d) => yScale(getPrice(d))}
            yScale={yScale}
            curve={curveStepAfter}
            fill="url(#cg-price-area)"
          />
          <LinePath<ChartPoint>
            data={data}
            x={(d) => xScale(getDate(d))}
            y={(d) => yScale(getPrice(d))}
            curve={curveStepAfter}
            stroke="var(--success)"
            strokeWidth={1.5}
            strokeLinejoin="miter"
          />
          <AxisLeft
            scale={yScale}
            numTicks={3}
            tickFormat={(v) => formatAxisPrice(v as number)}
            stroke="var(--line)"
            tickStroke="var(--line)"
            tickLabelProps={() => ({
              fill: 'var(--fg-2)',
              fontSize: 9,
              textAnchor: 'end',
              dx: -2,
              dy: 3,
            })}
          />
          <AxisBottom
            scale={xScale}
            top={INNER_H}
            tickValues={xTickValues}
            tickFormat={(v) => formatAxisDate(v as Date)}
            stroke="var(--line)"
            tickStroke="var(--line)"
            tickLabelProps={(_, index) => ({
              fill: 'var(--fg-2)',
              fontSize: 9,
              textAnchor:
                index === 0 ? 'start' : index === xTickValues.length - 1 ? 'end' : 'middle',
              dy: 2,
            })}
          />
          {atl && (
            <circle
              cx={xScale(atl.date)}
              cy={yScale(atl.priceInt)}
              r={2.5}
              fill="var(--success)"
            />
          )}
          {tooltipOpen && tooltipData && (
            <>
              <Line
                from={{ x: tooltipLeft, y: 0 }}
                to={{ x: tooltipLeft, y: INNER_H }}
                stroke="var(--fg-2)"
                strokeWidth={1}
                strokeDasharray="3,2"
                pointerEvents="none"
              />
              <circle
                cx={tooltipLeft}
                cy={tooltipTop}
                r={3.5}
                fill="var(--success)"
                stroke="var(--bg-1)"
                strokeWidth={1.5}
                pointerEvents="none"
              />
            </>
          )}
          <Bar
            width={INNER_W}
            height={INNER_H}
            fill="transparent"
            onMouseMove={handleTooltip}
            onTouchMove={handleTooltip}
            onTouchStart={handleTooltip}
            onMouseLeave={hideTooltip}
          />
        </Group>
      </svg>
      {tooltipOpen && tooltipData ? (
        <div
          className="cg-price-chart-tooltip"
          style={{
            left: Math.min(Math.max(tooltipLeft + MARGIN.left, 4), W - 4),
            top: tooltipTop + MARGIN.top,
          }}
        >
          <span className="cg-price-chart-tooltip-price">
            {formatPrice(tooltipData.priceInt, currency)}
          </span>
          {tooltipData.cut > 0 && (
            <span className="cg-price-chart-tooltip-cut">−{tooltipData.cut}%</span>
          )}
          <span className="cg-price-chart-tooltip-date">
            {tooltipData.date.toLocaleDateString(undefined, {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            })}
          </span>
        </div>
      ) : (
        atl && (
          <div className="cg-sparkline-meta">
            <span>ATL {formatPrice(atl.priceInt, currency)}</span>
            <span>
              {atl.date.toLocaleDateString(undefined, { year: 'numeric', month: 'short' })}
            </span>
          </div>
        )
      )}
    </div>
  )
}
